import { Component, inject } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import {
  MeteringPointCalculationParameterDto,
  MeteringPointMeteredDataSourceService
} from '@app/api/dmc';
import { FormApiService } from '@app/core/services/form-api.service';
import { HasUnsavedChanges } from '@app/core/guards/unsaved-changes.guard';
import { isoToRealDate } from '@app/core/utils/iso-to-real-date';
import { InputGroupComponent } from '@shared/components/input/input-group/input-group.component';
import { LabelDirective } from '@shared/components/input/label/label.directive';
import { InputDateDirective } from '@shared/components/input/date/input-date.directive';
import { dateLessThanEqualValidator } from '@shared/validators/date-less-than-equal.validator';
import { CalculationFormComponent } from '@views/calculation/calculation-form/calculation-form.component';

/** Makes a calculation formula the metering point's time-series source from the chosen date. */
@Component({
  selector: 'app-add-calculation-source-modal',
  imports: [
    ReactiveFormsModule,
    TranslatePipe,
    InputGroupComponent,
    LabelDirective,
    InputDateDirective,
    CalculationFormComponent,
  ],
  templateUrl: './add-calculation-source-modal.component.html',
})
export class AddCalculationSourceModalComponent implements HasUnsavedChanges {
  private readonly formApiService = inject(FormApiService);
  private readonly sourceService = inject(MeteringPointMeteredDataSourceService);

  /** Set by ModalService. */
  public meteringPointIdentification?: string;

  public readonly maxValidFrom = isoToRealDate(new Date());

  public readonly form = new FormGroup({
    validFrom: new FormControl<string | null>(isoToRealDate(new Date()), [Validators.required, dateLessThanEqualValidator(0)]),
    calculationStructureInternalName: new FormControl<string | null>(null, Validators.required),
    calculationStructureIsDynamic: new FormControl<boolean | null>(null),
  });

  /** Formula parameters, maintained by the calculation form. */
  public rowHandler: MeteringPointCalculationParameterDto[] = [];

  public hasUnsavedChanges(): boolean {
    return this.form.dirty;
  }

  public submit(): void {
    this.formApiService.submit<string>(
      this.form,
      data => this.sourceService.meteringPointMeteredDataSourceCalculationPost(data),
      {
        preprocess: () => ({
          meteringPointIdentification: this.meteringPointIdentification,
          validFrom: this.form.controls.validFrom.value,
          calculationStructureInternalName: this.form.controls.calculationStructureInternalName.value,
          parameters: this.rowHandler
            .filter(row => !!row.internalName && !!row.meteringPointIdentification?.trim())
            .map(row => ({ internalName: row.internalName!, meteringPointIdentification: row.meteringPointIdentification!.trim() })),
        }),
        successMessage: 'meteringpoint.source.addcalculation.success',
        closeModal: true,
      }
    );
  }
}
